// Page-level empty state for the Variations & SOWs tab. Surfaces only
// when the contract has zero SOWs and zero variations (and no archived
// rows are being requested). Per brief §6, the partial cases (SOWs
// only / variations only / all completed) keep both sections visible
// so the history is reachable; only the all-empty case replaces the
// register pair with a centred placeholder + two CTAs.
//
// The summary strip stays mounted above this state — it shows the
// contract value figure even when there are no derivative records — so
// users have one piece of useful context while they decide whether to
// add an SOW or log a variation directly against the contract.

import Button from "../../../common/Button";
import { Plus } from "lucide-react";

interface EmptyVariationSowStateProps {
  onAddSow: () => void;
  onAddVariation: () => void;
}

export default function EmptyVariationSowState({
  onAddSow,
  onAddVariation,
}: EmptyVariationSowStateProps) {
  return (
    <div
      data-testid="variation-sow-empty-state"
      className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800"
    >
      <h3 className="text-h3 text-slate-900 dark:text-slate-50">
        No SOWs or variations yet
      </h3>
      <p className="mt-2 text-body-sm text-slate-600 dark:text-slate-300">
        Track scoped engagements as SOWs and capture formal contract
        changes as variations. Both contribute to the total contract
        value visible above.
      </p>
      <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-3">
        {/* Both CTAs render with equal weight (secondary) so the empty state
            doesn't bias the user toward one path. Per-section header `+ Add`
            buttons stay primary in their own context. */}
        <Button
          variant="secondary"
          icon={<Plus className="h-4 w-4" />}
          onClick={onAddSow}
          data-testid="variation-sow-empty-add-sow"
        >
          Add SOW
        </Button>
        <Button
          variant="secondary"
          icon={<Plus className="h-4 w-4" />}
          onClick={onAddVariation}
          data-testid="variation-sow-empty-add-variation"
        >
          Add variation
        </Button>
      </div>
    </div>
  );
}
